/**
 * Current Plan Card Component
 * Shows current subscription status and management options
 */

import { Crown, ExternalLink, Loader2, CreditCard } from 'lucide-react';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { PRICING_TIERS } from '../utils/constants';
import type { Subscription, SubscriptionSource } from '../types';

interface CurrentPlanCardProps {
  subscription: Subscription | null;
  subscriptionSource: SubscriptionSource;
  isLoading: boolean;
  onManageSubscription: () => void;
}

export function CurrentPlanCard({
  subscription,
  subscriptionSource,
  isLoading,
  onManageSubscription,
}: CurrentPlanCardProps) {
  const { t } = useLanguage();

  const currentTier = subscription?.tier || 'free';
  const tierInfo = PRICING_TIERS.find((tier) => tier.id === currentTier);

  // Get source badge info
  const getSourceBadge = () => {
    switch (subscriptionSource) {
      case 'stripe':
        return { label: 'Stripe', bg: 'bg-indigo-100', text: 'text-indigo-700' };
      case 'apple':
        return { label: 'App Store', bg: 'bg-gray-100', text: 'text-gray-700' };
      case 'google':
        return { label: 'Google Play', bg: 'bg-green-100', text: 'text-green-700' };
      default:
        return null;
    }
  };

  const sourceBadge = getSourceBadge();

  return (
    <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-primary-200 transition-all duration-200 animate-fade-in animation-delay-100">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <CreditCard className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {t('upgrade.currentPlan.title', 'Current Plan')}
              </h2>
              <p className="text-sm text-gray-500">
                {t('upgrade.currentPlan.subtitle', 'Manage your subscription')}
              </p>
            </div>
          </div>

          {/* Tier badge */}
          <div className="flex items-center gap-2">
            {currentTier !== 'free' && (
              <div className="flex items-center gap-1 px-3 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium">
                <Crown className="w-4 h-4" />
                {tierInfo?.name}
              </div>
            )}
            {sourceBadge && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${sourceBadge.bg} ${sourceBadge.text}`}>
                {sourceBadge.label}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Current plan info */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-2xl font-bold text-gray-900">{tierInfo?.name || 'Free'}</p>
            <p className="text-sm text-gray-500">{tierInfo?.description}</p>
          </div>
        </div>

        {/* Period end date */}
        {subscription?.current_period_end && (
          <p className="text-sm text-gray-500 mb-4">
            {t('upgrade.currentPlan.periodEnds', 'Current period ends:')}{' '}
            <span className="font-medium text-gray-700">
              {new Date(subscription.current_period_end).toLocaleDateString()}
            </span>
          </p>
        )}

        {/* App Store subscription notice */}
        {subscriptionSource === 'apple' && (
          <div className="p-4 bg-gray-50 rounded-xl mb-4">
            <p className="text-sm text-gray-600">
              {t('upgrade.source.apple', 'Your subscription was purchased through the App Store.')}
            </p>
            <a
              href="https://apps.apple.com/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              {t('upgrade.manageAppStore', 'Manage in App Store')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Google Play subscription notice */}
        {subscriptionSource === 'google' && (
          <div className="p-4 bg-gray-50 rounded-xl mb-4">
            <p className="text-sm text-gray-600">
              {t('upgrade.source.google', 'Your subscription was purchased through Google Play.')}
            </p>
            <a
              href="https://play.google.com/store/account/subscriptions"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
            >
              {t('upgrade.manageGooglePlay', 'Manage in Google Play')}
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}

        {/* Stripe manage button */}
        {subscriptionSource === 'stripe' && (
          <button
            onClick={onManageSubscription}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all disabled:opacity-50"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4" />
            )}
            {t('upgrade.manageBilling', 'Manage Billing')}
          </button>
        )}

        {/* Free tier message */}
        {subscriptionSource === 'none' && currentTier === 'free' && (
          <p className="text-sm text-gray-500">
            {t('upgrade.currentPlan.freeMessage', "You're on the free plan. Upgrade to unlock more features!")}
          </p>
        )}
      </div>
    </div>
  );
}
